import React, {useCallback, useEffect, useState} from "react";
import {Alert, Button, Checkbox, Descriptions, Modal, Select, Spin} from "antd";
import {getBands} from "../../bands/bands.service";
import accountApi from "../../accounts/accounts.service";
import {ModalContentWrapper} from "../../common/modal/modal.style";
import {getBandLabel, getBandName} from "./collectorProfileView";

export const getProfileFormState = (response) => {
  const selectedBandIds = response.profiles.map((profile) => profile.band.id);
  const defaultProfile = response.profiles.find((profile) => profile.isDefault) || response.profiles.find(
    (profile) => profile.id === response.defaultProfileId
  );

  return {
    selectedBandIds,
    defaultBandId: defaultProfile?.band.id || "",
  };
};

export const getProfileChangeConfirmation = ({bands, previousBandIds, selectedBandIds, defaultBandId}) => {
  if (previousBandIds.length === 1 && selectedBandIds.length > 1) {
    return {
      title: "복수 밴드 계정으로 변경하시겠습니까?",
      content: `소속 밴드: ${selectedBandIds.map((bandId) => getBandName(bands, bandId)).join(", ")} / 기본 밴드: ${getBandName(bands, defaultBandId)}`,
    };
  }

  if (previousBandIds.length > 1 && selectedBandIds.length === 1) {
    return {
      title: `소속 밴드를 ${getBandName(bands, selectedBandIds[0])} 하나로 변경하시겠습니까?`,
      content: "선택하지 않은 밴드는 소속 밴드에서 제외됩니다.",
    };
  }

  if (previousBandIds.length === 1 && selectedBandIds.length === 1 && previousBandIds[0] !== selectedBandIds[0]) {
    return {
      title: "소속 밴드를 변경하시겠습니까?",
      content: `${getBandName(bands, previousBandIds[0])} → ${getBandName(bands, selectedBandIds[0])}`,
    };
  }

  return {
    title: "소속 밴드 설정을 저장하시겠습니까?",
    content: `소속 밴드: ${selectedBandIds.map((bandId) => getBandName(bands, bandId)).join(", ")} / 기본 밴드: ${getBandName(bands, defaultBandId)}`,
  };
};

const CollectorProfilesModal = ({modalState, setModalState, setForceReload}) => {
  const collector = modalState.modalData;
  const [bands, setBands] = useState([]);
  const [profiles, setProfiles] = useState(null);
  const [selectedBandIds, setSelectedBandIds] = useState([]);
  const [defaultBandId, setDefaultBandId] = useState("");
  const [multiBandMode, setMultiBandMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bandsLoading, setBandsLoading] = useState(true);
  const [bandsLoadFailed, setBandsLoadFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const hideModal = useCallback(() => {
    setModalState({isVisible: false, modalData: {}, modalType: ""});
  }, [setModalState]);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await accountApi.getCollectorProfiles(collector.id);
      setProfiles(response);
      const formState = getProfileFormState(response);
      setSelectedBandIds(formState.selectedBandIds);
      setDefaultBandId(formState.defaultBandId);
      setMultiBandMode(formState.selectedBandIds.length > 1);
    } catch (error) {
      alert("콜랙터 프로필 정보를 불러오지 못했습니다.");
      hideModal();
    } finally {
      setLoading(false);
    }
  }, [collector.id, hideModal]);

  useEffect(() => {
    if (!collector.id) return;
    setBandsLoading(true);
    setBandsLoadFailed(false);
    getBands()
      .then(setBands)
      .catch(() => setBandsLoadFailed(true))
      .finally(() => setBandsLoading(false));
    loadProfiles();
  }, [collector.id, loadProfiles]);

  const handleBandsChange = (bandIds) => {
    setSelectedBandIds(bandIds);
    if (!bandIds.includes(defaultBandId)) setDefaultBandId(bandIds[0] || "");
  };

  const handleMultiBandModeChange = (event) => {
    const checked = event.target.checked;
    setMultiBandMode(checked);
    if (!checked) {
      const remainingBandId = defaultBandId || selectedBandIds[0] || "";
      setSelectedBandIds(remainingBandId ? [remainingBandId] : []);
      setDefaultBandId(remainingBandId);
    }
  };

  const saveProfiles = async () => {
    setSubmitting(true);
    try {
      const response = await accountApi.updateCollectorProfiles(collector.id, {
        bandIds: selectedBandIds,
        defaultBandId,
      });
      setProfiles(response);
      const formState = getProfileFormState(response);
      setSelectedBandIds(formState.selectedBandIds);
      setDefaultBandId(formState.defaultBandId);
      setMultiBandMode(formState.selectedBandIds.length > 1);
      setForceReload();
      alert("소속 밴드가 저장되었습니다.");
      hideModal();
    } catch (error) {
      alert("소속 밴드 저장에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (bandsLoading || bandsLoadFailed) return alert("밴드 정보를 불러온 후 다시 저장해주세요.");
    if (selectedBandIds.length === 0) return alert("소속 밴드를 선택해주세요.");
    if (multiBandMode && selectedBandIds.length < 2) return alert("복수 밴드는 두 개 이상 선택해주세요.");
    if (!defaultBandId || !selectedBandIds.includes(defaultBandId)) {
      return alert("기본 밴드는 선택한 밴드 중에서 지정해주세요.");
    }

    const confirmation = getProfileChangeConfirmation({
      bands,
      previousBandIds: profiles.profiles.map((profile) => profile.band.id),
      selectedBandIds,
      defaultBandId,
    });

    Modal.confirm({
      ...confirmation,
      okText: "변경",
      cancelText: "취소",
      onOk: saveProfiles,
    });
  };

  return (
    <Modal
      title="소속 밴드 관리"
      visible={modalState.isVisible}
      onCancel={hideModal}
      width={620}
      footer={[
        <Button key="cancel" onClick={hideModal}>취소</Button>,
        <Button key="submit" type="primary" loading={submitting} disabled={loading || bandsLoading || bandsLoadFailed} onClick={handleSubmit}>
          저장
        </Button>,
      ]}
    >
      {loading || !profiles ? <Spin/> : (
        <ModalContentWrapper>
          {bandsLoadFailed && <Alert
            type="error"
            showIcon
            message="밴드 정보를 불러오지 못했습니다. 저장할 수 없습니다."
            style={{width: "100%", margin: "8px"}}
          />}
          <Descriptions bordered size="small" column={1} style={{width: "100%"}}>
            <Descriptions.Item label="소속 밴드">
              <div style={{display: "flex", alignItems: "center", gap: "12px"}}>
                <Select
                  mode={multiBandMode ? "multiple" : undefined}
                  value={multiBandMode ? selectedBandIds : selectedBandIds[0] || undefined}
                  onChange={(value) => multiBandMode
                    ? handleBandsChange(value)
                    : handleBandsChange(value ? [value] : [])}
                  disabled={bandsLoading || bandsLoadFailed}
                  style={{width: "100%"}}
                  placeholder="소속 밴드를 선택해주세요"
                  showSearch
                  filterOption={(input, option) =>
                    String(option.children ?? "").toLowerCase().includes(input.toLowerCase())}
                >
                  {bands.map((band) => (
                    <Select.Option key={band.id} value={band.id}>{getBandLabel(band)}</Select.Option>
                  ))}
                </Select>
                <Checkbox
                  checked={multiBandMode}
                  onChange={handleMultiBandModeChange}
                  disabled={bandsLoading || bandsLoadFailed}
                  style={{whiteSpace: "nowrap"}}
                >
                  복수 밴드
                </Checkbox>
              </div>
            </Descriptions.Item>
            {multiBandMode && <Descriptions.Item label="기본 밴드">
              <Select
                value={defaultBandId || undefined}
                onChange={setDefaultBandId}
                disabled={bandsLoading || bandsLoadFailed}
                style={{width: "100%"}}
                placeholder="기본 밴드를 선택해주세요"
              >
                {bands.filter((band) => selectedBandIds.includes(band.id)).map((band) => (
                  <Select.Option key={band.id} value={band.id}>{getBandLabel(band)}</Select.Option>
                ))}
              </Select>
            </Descriptions.Item>}
          </Descriptions>
        </ModalContentWrapper>
      )}
    </Modal>
  );
};

export default CollectorProfilesModal;
